/**
 * Zod schemas + DTO types for the Profile API surface — Phase 3 (W1.T2).
 *
 * `GET /profile/me` returns the authenticated user's public summary plus
 * any active absences (today_msk inside [dateFrom, dateTo]) plus a
 * preferences placeholder. Preferences are deliberately empty in M1 —
 * shape is reserved so M2 additions land without an API version bump.
 *
 * Visibility matrix at the service layer (D01 / OQ-D §15.3):
 *   - self                → 200 (any access level, including SUPERVISOR
 *                                  for own profile)
 *   - РО (MANAGER) own-dept → 200
 *   - ADMIN any            → 200
 *   - SUPERVISOR other     → 403 `supervisor_no_org_data`
 *   - other Сотрудник      → 403
 */
import { z } from 'zod';
import { AbsenceRowSchema } from './absence';
import { UserSummarySchema } from './users';

const UuidSchema = z.string().uuid();

/**
 * `GET /profile/:userId` query. M1 has no extra knobs — schema is kept
 * for forward compatibility (and uniformity with the workload / absence
 * surface).
 */
export const ProfileQuerySchema = z
  .object({
    userId: UuidSchema.optional(),
  })
  .strict();
export type ProfileQuery = z.infer<typeof ProfileQuerySchema>;

/**
 * Preferences placeholder. Empty object in M1. Concrete fields will land
 * in M2 (e.g. `defaultGridView: 'week' | 'month'`); any unknown keys are
 * ignored on read so older clients tolerate forward additions.
 */
export const ProfilePreferencesSchema = z.object({}).passthrough();
export type ProfilePreferences = z.infer<typeof ProfilePreferencesSchema>;

/** Composed profile envelope. */
export const ProfileResponseSchema = z.object({
  user: UserSummarySchema,
  activeAbsences: z.array(AbsenceRowSchema),
  preferences: ProfilePreferencesSchema,
});
export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;
